// react-i18next setup. Default UI language detected from the browser
// pre-bond on the first visit, then persisted to localStorage so a
// returning visitor keeps whatever toggle they chose. Once a Bond is
// loaded, the post-bond surfaces switch to bond.locale via
// switchLocaleToBond regardless of browser language — the Bond's
// locale is locked at awakening and outlives the browser.

import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { en } from './en.js';
import { es } from './es.js';

export type AppLocale = 'en' | 'es';

const resources = {
  en: { translation: en },
  es: { translation: es },
} as const;

export function detectBrowserLocale(): AppLocale {
  const lang = (navigator.language ?? 'en').toLowerCase();
  return lang.startsWith('es') ? 'es' : 'en';
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'es'],
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      // Order matters: localStorage first so a returning visitor's
      // toggle wins over the navigator's locale, navigator second
      // for cold first visits. The localStorage key is the i18next
      // default 'i18nextLng'.
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    returnNull: false,
  });

export function switchLocaleToBond(locale: AppLocale | undefined | null): void {
  if (!locale) return;
  if (i18n.language === locale) return;
  void i18n.changeLanguage(locale);
}

/** Imperative toggle used by the pre-bond UI selector. Persists via
 *  the localStorage cache configured above. */
export function setUserLocale(locale: AppLocale): void {
  if (i18n.language === locale) return;
  void i18n.changeLanguage(locale);
}

export default i18n;
